import { useGetIdentity, useGetList, type Identifier } from "ra-core";
import {
  CalendarDays,
  Check,
  ChevronDown,
  ChevronRight,
  History,
  ListChecks,
  MessageSquare,
  Play,
  UserPlus,
  Wrench,
  X,
} from "lucide-react";
import { useIsMobile } from "@/hooks/use-mobile";
import { lazy, Suspense, useState } from "react";
import { Link, useNavigate } from "react-router";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerHeader,
  DrawerTitle,
} from "@/components/ui/drawer";
import { Skeleton } from "@/components/ui/skeleton";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import {
  NextStepDialog,
  saleName,
  useActiveSales,
} from "../../companies/NextStepDialog";
import { DealNextStepDialog } from "../../deals/DealNextStepDialog";
import { Kbd } from "../../layout/Kbd";
import { Page } from "../../layout/Page";
import { routePaths } from "../../root/routePaths";
import { TripletexSyncDialog } from "../../layout/TripletexSyncDialog";
import { addDaysKey, formatDateShort } from "../../misc/osloDate";
import type { CompanyNextStep, Deal, Sale, TimeEntry } from "../../types";
import { entrySeconds, formatDuration } from "../../workItems/timeFormat";
import { useTimer } from "../../workItems/useTimer";
import { openDriftFindings, type DriftScan } from "./buildRows";
import {
  ContactForm,
  contactDraftError,
  emptyContactDraft,
  type ContactDraft,
} from "./ContactForm";
import { CustomerPanel } from "./CustomerPanel";
import { CustomerTable, customerRows } from "./CustomerTable";
import { MobileRowSheet } from "./MobileRowSheet";
import { TeamWorkload } from "./TeamWorkload";
import { DriftFindings } from "./DriftFindings";
import { FocusRowItem, type RowMenuItem } from "./FocusRow";
import { PipelineStrip } from "./PipelineStrip";
import { RankingPopover } from "./RankingPopover";
import {
  rankRows,
  ROW_TYPE_LABEL,
  VISIBLE_ROWS,
  type FocusRow,
  type Scope,
} from "./ranking";
import { useDashboardPrefs, type DashboardMode } from "./useDashboardPrefs";
import { useFocusData } from "./useFocusData";
import {
  canTake,
  dealOf,
  dueTarget,
  nextMonday,
  QUICK_DATES,
  useFocusActions,
  weekMonday,
  workItemOf,
} from "./useFocusActions";
import { usePins } from "./usePins";

const DashboardWidget = lazy(() =>
  import("../DashboardWidget").then((m) => ({ default: m.DashboardWidget })),
);

// Oversikt «Fokus» (spesifikasjonen v1.0, 25.10.2026): én rangert liste over
// hva som bør gjøres nå, med en forklaring på hver rad. Det som har ligget
// over 30 dager, står for seg i «Rydd opp». Høyre kolonne viser pipelinen
// og notatet, eller kundepanelet når en rad er åpnet (M2). Ingen
// nøkkeltall-kort: tallene står i én setning øverst.

const SCOPES: { value: Scope; label: string }[] = [
  { value: "mine", label: "Mine" },
  { value: "team", label: "Team" },
  { value: "unowned", label: "Uten eier" },
];

const greetingFor = (hour: number) =>
  hour < 6
    ? "God natt"
    : hour < 11
      ? "God morgen"
      : hour < 18
        ? "God dag"
        : "God kveld";

const WEEKDAYS = [
  "Søndag",
  "Mandag",
  "Tirsdag",
  "Onsdag",
  "Torsdag",
  "Fredag",
  "Lørdag",
];

const isOverdue = (row: FocusRow) =>
  row.signals.some(
    (s) => s.kind === "task_overdue" || s.kind === "next_step_overdue",
  );

const plural = (n: number, one: string, many: string) =>
  `${n} ${n === 1 ? one : many}`;

/** Handlingene en rad har (M6). Knappene viser opptil tre av dem. */
type ActionId =
  | "complete"
  | "postpone"
  | "timer"
  | "plan"
  | "contact"
  | "maintain"
  | "findings"
  | "makeTasks";

const rowButtons = (row: FocusRow): ActionId[] => {
  const kind = row.key.split(":")[0];
  if (row.type === "notification") return ["complete"];
  if (row.type === "agreement") return ["maintain"];
  if (row.type === "drift") return ["findings", "makeTasks"];
  if (kind === "task") return ["complete", "postpone", "timer"];
  if (kind === "deal" && row.type === "next_step")
    return ["complete", "postpone", "contact"];
  if (kind === "step" || kind === "note")
    return ["complete", "postpone", "contact"];
  if (row.type === "opportunity" || row.type === "lead")
    return ["plan", "contact"];
  return ["contact"];
};

const focusRowElement = (key: string) =>
  document.querySelector<HTMLElement>(`[data-row-key="${CSS.escape(key)}"]`);

export const FocusDashboard = () => {
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const data = useFocusData();
  const prefs = useDashboardPrefs();
  const actions = useFocusActions();
  const pins = usePins();
  const timer = useTimer();
  const { data: sales = [] } = useActiveSales();
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const [showAll, setShowAll] = useState(false);
  const [panelOpen, setPanelOpen] = useState(false);
  const [panelCompany, setPanelCompany] = useState<Identifier | null>(null);
  const [contact, setContact] = useState<{
    key: string;
    draft: ContactDraft;
    error?: string | null;
  } | null>(null);
  const [drift, setDrift] = useState<{
    key: string;
    preselect: boolean;
  } | null>(null);
  const [postponeKey, setPostponeKey] = useState<string | null>(null);
  const [planDeal, setPlanDeal] = useState<Deal | null>(null);
  const [planCompany, setPlanCompany] = useState<Identifier | null>(null);
  const [timerRow, setTimerRow] = useState<FocusRow | null>(null);
  const [announcement, setAnnouncement] = useState("");
  // Team: et klikk på en person i «Teamet» viser bare den personens rader.
  const [person, setPerson] = useState<Identifier | null>(null);
  // Mobil: handlingsarket for raden som ble trykket på (kap. 13).
  const [sheetRow, setSheetRow] = useState<FocusRow | null>(null);
  const [customerKey, setCustomerKey] = useState<string | null>(null);

  const scope = prefs.scope;
  // Kunder, Team-linjene og ukeplanen er bare for stor skjerm i første
  // versjon (kap. 13).
  const mode = isMobile ? "items" : prefs.mode;
  const ranked = rankRows(data.rows, {
    scope,
    me: data.me,
    pins: pins.keys,
  });
  const byPerson = (rows: FocusRow[]) =>
    scope === "team" && person != null
      ? rows.filter((r) => String(r.ownerId) === String(person))
      : rows;
  const now = byPerson(ranked.now);
  const cleanup = byPerson(ranked.cleanup);
  const visible = showAll ? now : now.slice(0, VISIBLE_ROWS);
  const selected =
    visible.find((r) => r.key === selectedKey)?.key ?? visible[0]?.key;
  const selectedRow = visible.find((r) => r.key === selected);
  const customers = customerRows([...now, ...cleanup]);
  const selectedCustomer =
    customers.find((c) => c.key === customerKey) ?? customers[0];
  // Panelet følger valgt rad; en rad uten kunde beholder forrige kunde.
  const panelCompanyId =
    panelOpen && !isMobile
      ? mode === "customers"
        ? (selectedCustomer?.companyId ?? panelCompany)
        : (selectedRow?.companyId ?? panelCompany)
      : null;

  // Bytte mellom Oppgaver og Kunder beholder valgt kunde (M3).
  const switchMode = (next: DashboardMode) => {
    if (next === "customers" && selectedRow?.companyId != null) {
      setCustomerKey(`cust:${selectedRow.companyId}`);
    }
    if (next === "items" && selectedCustomer) {
      const row = now.find(
        (r) => String(r.companyId) === String(selectedCustomer.companyId),
      );
      if (row) setSelectedKey(row.key);
    }
    prefs.save({ mode: next });
  };

  const announce = (text: string) => {
    // Samme tekst to ganger på rad skal også leses opp.
    setAnnouncement("");
    window.setTimeout(() => setAnnouncement(text), 50);
  };

  const ownerName = (row: FocusRow) =>
    saleName(sales as Sale[], row.ownerId) ?? null;

  const openPanel = (row: FocusRow) => {
    setSelectedKey(row.key);
    if (isMobile) {
      setSheetRow(row);
      return;
    }
    if (row.companyId == null) {
      navigate(row.path);
      return;
    }
    setPanelCompany(row.companyId);
    setPanelOpen(true);
  };

  const closePanel = () => {
    setPanelOpen(false);
    const key = mode === "customers" ? selectedCustomer?.key : selected;
    if (key) focusRowElement(key)?.focus();
  };

  const openContact = (row: FocusRow) => {
    if (row.companyId == null) return;
    setSelectedKey(row.key);
    setDrift(null);
    setContact((prev) =>
      prev?.key === row.key
        ? prev
        : { key: row.key, draft: emptyContactDraft() },
    );
  };

  // Lagringen er optimistisk (fiks 1): skjemaet lukkes med en gang og åpnes
  // igjen med teksten og F-referansen hvis lagringen feiler.
  const saveContact = (row: FocusRow) => {
    if (!contact || contact.key !== row.key || row.companyId == null) return;
    const { draft } = contact;
    if (contactDraftError(draft)) return;
    setContact(null);
    focusRowElement(row.key)?.focus();
    const deal = dealOf(row);
    actions
      .logContact({
        companyId: row.companyId,
        type: draft.type,
        note: draft.note.trim() || undefined,
        nextStep: draft.nextStep.trim() || undefined,
        due: draft.nextStep.trim() ? draft.due || undefined : undefined,
        dealId: deal?.id,
      })
      .then(() =>
        announce(`Kontakt registrert hos ${row.companyName ?? row.title}`),
      )
      .catch((error: Error) => {
        setContact({
          key: row.key,
          draft,
          error: error?.message || "Kunne ikke lagre kontakten.",
        });
      });
  };

  const plan = (row: FocusRow) => {
    const deal = dealOf(row);
    if (deal) setPlanDeal(deal);
    else if (row.companyId != null) setPlanCompany(row.companyId);
  };

  const startTimer = (row: FocusRow) => {
    const item = workItemOf(row);
    if (!item) return;
    const running = timer.runningEntry;
    if (running && String(running.work_item_id) === String(item.id)) {
      announce("Timeren går allerede på denne oppgaven");
      return;
    }
    if (running) {
      setTimerRow(row);
      return;
    }
    void timer
      .start(item.id)
      .then(() => announce(`Timer startet: ${item.title}`));
  };

  const togglePin = async (row: FocusRow) => {
    const pinned = await pins.toggle(row.key);
    announce(
      pinned ? `${row.title} er festet øverst i dag` : `${row.title} er løsnet`,
    );
  };

  // Toasten med «Angre» leses opp av varslingsområdet sitt.
  const take = (row: FocusRow) => {
    if (data.me != null) void actions.take(row, data.me);
  };

  const run = (id: ActionId, row: FocusRow) => {
    switch (id) {
      case "complete":
        return void actions.complete(row);
      case "postpone":
        setSelectedKey(row.key);
        return setPostponeKey(row.key);
      case "timer":
        return startTimer(row);
      case "plan":
        return plan(row);
      case "contact":
        return openContact(row);
      case "maintain":
        return void actions.markMaintained(row, data.today);
      case "findings":
      case "makeTasks":
        setContact(null);
        setSelectedKey(row.key);
        return setDrift((prev) =>
          prev?.key === row.key && id === "findings"
            ? null
            : { key: row.key, preselect: id === "makeTasks" },
        );
    }
  };

  const available = (id: ActionId, row: FocusRow) => {
    switch (id) {
      case "complete":
        return row.type === "notification" || dueTarget(row) != null;
      case "postpone":
        return dueTarget(row) != null;
      case "timer":
        return workItemOf(row) != null;
      case "plan":
        return dealOf(row) != null || row.companyId != null;
      case "contact":
        return row.companyId != null;
      default:
        return true;
    }
  };

  const ACTION_LABEL: Record<ActionId, string> = {
    complete: "Fullfør",
    postpone: "Utsett",
    timer: "Start timer",
    plan: "Planlegg neste steg",
    contact: "Registrer kontakt",
    maintain: "Registrer vedlikehold",
    findings: "Vis funn",
    makeTasks: "Lag oppgaver",
  };
  const ACTION_KEY: Partial<Record<ActionId, string>> = {
    complete: "F",
    postpone: "U",
    timer: "S",
    plan: "N",
    contact: "R",
  };
  const ACTION_ICON = {
    complete: Check,
    postpone: CalendarDays,
    timer: Play,
    plan: CalendarDays,
    contact: MessageSquare,
    maintain: Wrench,
    findings: ListChecks,
    makeTasks: ListChecks,
  } satisfies Record<ActionId, unknown>;

  const labelFor = (id: ActionId, row: FocusRow) =>
    id === "complete" && row.type === "notification"
      ? "Marker som lest"
      : id === "findings" && drift?.key === row.key
        ? "Skjul funn"
        : ACTION_LABEL[id];

  const buttonsFor = (row: FocusRow, light = false) =>
    rowButtons(row)
      .filter((id) => available(id, row))
      .slice(0, 3)
      .map((id) => {
        const Icon = ACTION_ICON[id];
        const button = (
          <Button
            key={id}
            variant={light ? "ghost" : "outline"}
            size="sm"
            tabIndex={light ? -1 : undefined}
            className="h-8 px-2.5"
            aria-label={labelFor(id, row)}
            title={`${labelFor(id, row)}${ACTION_KEY[id] ? ` (${ACTION_KEY[id]})` : ""}`}
            onClick={id === "postpone" ? undefined : () => run(id, row)}
          >
            <Icon aria-hidden className="size-3.5" />
            {/* Smale skjermer: bare ikonet, så tittelen får plass. */}
            <span className="hidden min-[1440px]:inline">
              {labelFor(id, row)}
            </span>
            {id === "postpone" && (
              <ChevronDown aria-hidden className="size-3" />
            )}
          </Button>
        );
        if (id !== "postpone" || light) return button;
        return (
          <DropdownMenu
            key={id}
            open={postponeKey === row.key}
            onOpenChange={(open) => setPostponeKey(open ? row.key : null)}
          >
            <DropdownMenuTrigger asChild>{button}</DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {QUICK_DATES(data.today).map((q) => (
                <DropdownMenuItem
                  key={q.label}
                  onSelect={() => void actions.move(row, q.date)}
                >
                  {q.label} ({formatDateShort(q.date)})
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        );
      });

  const menuFor = (row: FocusRow): RowMenuItem[] => {
    const items: RowMenuItem[] = [
      {
        label: "Åpne",
        onSelect: () => navigate(row.path),
        shortcut: "⇧ Enter",
      },
    ];
    if (row.companyId != null && !isMobile) {
      items.push({
        label: "Vis kundepanel",
        onSelect: () => openPanel(row),
        shortcut: "Enter",
      });
    }
    items.push({
      label: row.pinOrder != null ? "Løsne" : "Fest øverst i dag",
      onSelect: () => void togglePin(row),
      shortcut: "P",
    });
    items.push("separator");
    if (canTake(row)) {
      items.push({ label: "Ta denne", onSelect: () => take(row) });
    }
    const all: ActionId[] = [
      "complete",
      "timer",
      "plan",
      "contact",
      ...(row.type === "agreement" ? (["maintain"] as const) : []),
      ...(row.type === "drift" ? (["findings", "makeTasks"] as const) : []),
    ];
    for (const id of all) {
      if (!available(id, row)) continue;
      if (
        (id === "maintain" && row.type !== "agreement") ||
        ((id === "findings" || id === "makeTasks") && row.type !== "drift")
      )
        continue;
      if (id === "complete" && row.type !== "notification" && !dueTarget(row))
        continue;
      items.push({
        label: labelFor(id, row),
        onSelect: () => run(id, row),
        shortcut: ACTION_KEY[id],
      });
    }
    if (dueTarget(row)) {
      items.push({
        label: "Utsett",
        items: QUICK_DATES(data.today).map((q) => ({
          label: `${q.label} (${formatDateShort(q.date)})`,
          onSelect: () => void actions.move(row, q.date),
        })),
      });
    }
    if (row.companyId != null && row.type !== "customer") {
      items.push({
        label: "Åpne kunde",
        onSelect: () => navigate(`/companies/${row.companyId}/show`),
      });
    }
    return items;
  };

  // Hurtigtastene på valgt rad (kap. 10). Én bokstav virker bare når fokus
  // står på raden selv, aldri i et tekstfelt.
  const onRowKey = (row: FocusRow, event: React.KeyboardEvent) => {
    if (event.ctrlKey || event.metaKey || event.altKey) return;
    const key = event.key.toLowerCase();
    const map: Record<string, () => void> = {
      f: () => available("complete", row) && run("complete", row),
      u: () => available("postpone", row) && run("postpone", row),
      n: () => available("plan", row) && run("plan", row),
      r: () => available("contact", row) && run("contact", row),
      s: () => available("timer", row) && run("timer", row),
      p: () => void togglePin(row),
      escape: () => {
        if (contact) setContact(null);
        else if (drift) setDrift(null);
        else if (panelOpen) setPanelOpen(false);
      },
    };
    if (event.shiftKey && key !== "escape") return;
    const handler = map[key];
    if (!handler) return;
    event.preventDefault();
    event.stopPropagation();
    handler();
  };

  const contactRow = contact
    ? (now.find((r) => r.key === contact.key) ?? null)
    : null;

  const header = <Greeting data={data} scope={scope} rows={now} />;

  if (data.isPending || prefs.isPending) {
    return (
      <FocusLayout header={header} aside={null}>
        <div aria-label="Laster Oversikt" className="space-y-2">
          {Array.from({ length: 6 }, (_, i) => (
            <Skeleton key={i} className="h-[58px] w-full" />
          ))}
        </div>
      </FocusLayout>
    );
  }

  if (!data.hasAnyData) {
    return (
      <FocusLayout header={header} aside={null}>
        <NewInstance />
      </FocusLayout>
    );
  }

  const driftFor = (companyId: Identifier | null | undefined) =>
    companyId == null
      ? null
      : (data.drift.find((d) => String(d.companyId) === String(companyId)) ??
        null);

  const aside =
    panelCompanyId != null ? (
      <CustomerPanel
        companyId={panelCompanyId}
        openDeals={data.openDeals}
        tasks={data.tasks}
        drift={driftFor(panelCompanyId)}
        onClose={closePanel}
        onPlan={(deal) =>
          deal ? setPlanDeal(deal) : setPlanCompany(panelCompanyId)
        }
        onContact={() => {
          const row =
            selectedRow?.companyId != null &&
            String(selectedRow.companyId) === String(panelCompanyId)
              ? selectedRow
              : visible.find(
                  (r) => String(r.companyId) === String(panelCompanyId),
                );
          if (row) openContact(row);
          else setPlanCompany(panelCompanyId);
        }}
      />
    ) : (
      <>
        <PipelineStrip deals={data.openDeals} rows={data.rows} />
        <NoteCard />
      </>
    );

  return (
    <FocusLayout header={header} aside={aside}>
      <div aria-live="polite" role="status" className="sr-only">
        {announcement}
      </div>
      <Controls
        scope={scope}
        mode={mode}
        showMode={!isMobile}
        remembered={prefs.remembered}
        onScope={(value) => {
          prefs.save({ scope: value });
          setShowAll(false);
          setPerson(null);
        }}
        onMode={switchMode}
      />

      {data.isError && (
        <div
          role="alert"
          className="flex items-center gap-3 rounded-[10px] border p-4 text-sm"
        >
          Kunne ikke hente alt til Oversikten.
          <Button variant="outline" size="sm" onClick={data.refetch}>
            Prøv igjen
          </Button>
        </div>
      )}

      {scope === "team" && !isMobile && (
        <TeamWorkload
          sales={sales as Sale[]}
          rows={[...ranked.now, ...ranked.cleanup]}
          monday={weekMonday(data.today)}
          person={person}
          onPerson={(id) => {
            setPerson(id);
            setShowAll(false);
          }}
        />
      )}
      {person != null && scope === "team" && (
        <p className="-mt-2 text-[13px] text-muted-foreground">
          Viser bare {saleName(sales as Sale[], person) ?? "én person"}.{" "}
          <button
            type="button"
            onClick={() => setPerson(null)}
            className="underline underline-offset-4 hover:text-foreground"
          >
            Vis hele teamet
          </button>
        </p>
      )}

      {mode === "customers" ? (
        <CustomerTable
          ranked={[...now, ...cleanup]}
          scope={scope}
          me={data.me}
          today={data.today}
          companies={data.companies}
          openDeals={data.openDeals}
          tasks={data.tasks}
          steps={data.customerSteps}
          drift={data.drift}
          selected={selectedCustomer?.key ?? null}
          onSelect={(row) => setCustomerKey(row.key)}
          onOpen={(row) => {
            setCustomerKey(row.key);
            setPanelCompany(row.companyId);
            setPanelOpen(true);
          }}
          onOpenFull={(row) => navigate(`/companies/${row.companyId}/show`)}
        />
      ) : (
        <>
          {now.length === 0 && cleanup.length === 0 ? (
            <UpToDate
              scope={scope}
              tasks={data.tasks}
              today={data.today}
              onTeam={() => prefs.save({ scope: "team" })}
              isAdmin={data.isAdmin}
              hasRowsElsewhere={data.rows.length > 0}
            />
          ) : (
            <section
              aria-labelledby="gjor-na-title"
              className="flex flex-col gap-3"
            >
              <div className="flex items-baseline justify-between gap-3">
                <h2 id="gjor-na-title" className="text-[13px] font-semibold">
                  {scope === "team" ? "Gjør nå for teamet" : "Gjør nå"}
                  <span className="ml-2 font-normal text-muted-foreground">
                    {now.length > VISIBLE_ROWS && !showAll
                      ? `${VISIBLE_ROWS} av ${now.length}, rangert`
                      : `${now.length}, rangert`}
                    {scope !== "mine" && now.some((r) => r.unowned)
                      ? " · uten eier øverst"
                      : ""}
                  </span>
                </h2>
                {now.length > VISIBLE_ROWS && (
                  <button
                    type="button"
                    onClick={() => setShowAll((v) => !v)}
                    className="text-[13px] text-muted-foreground hover:text-foreground"
                  >
                    {showAll ? "Vis færre" : `Vis alle ${now.length}`}
                  </button>
                )}
              </div>
              {now.length === 0 ? (
                <p className="rounded-[10px] border p-4 text-sm text-muted-foreground">
                  Ingenting haster nå. Det som har ligget lenge, står i Rydd opp
                  under.
                </p>
              ) : (
                <ul
                  role="list"
                  aria-label="Gjør nå"
                  className="overflow-hidden rounded-[10px] border bg-card"
                >
                  {visible.map((row) => {
                    const driftScan =
                      drift?.key === row.key ? (row.source as DriftScan) : null;
                    return (
                      <FocusRowItem
                        key={row.key}
                        row={row}
                        selected={row.key === selected}
                        owner={scope === "team" ? ownerName(row) : null}
                        menu={menuFor(row)}
                        actions={buttonsFor(row)}
                        hoverActions={buttonsFor(row, true)}
                        pinnedActions={
                          scope !== "mine" && canTake(row) ? (
                            <Button
                              size="sm"
                              className="h-8 px-2.5"
                              onClick={() => take(row)}
                            >
                              <UserPlus aria-hidden className="size-3.5" />
                              <span className="sr-only sm:not-sr-only">
                                Ta denne
                              </span>
                            </Button>
                          ) : null
                        }
                        onSelect={() => setSelectedKey(row.key)}
                        onActivate={() => openPanel(row)}
                        onOpen={() => openPanel(row)}
                        onOpenFull={() => navigate(row.path)}
                        onKeyDown={(event) => onRowKey(row, event)}
                      >
                        {!isMobile && contact?.key === row.key && (
                          <ContactForm
                            id={`kontakt-${row.key.replace(":", "-")}`}
                            draft={contact.draft}
                            error={contact.error}
                            isDeal={dealOf(row) != null}
                            onChange={(draft) =>
                              setContact({ ...contact, draft, error: null })
                            }
                            onSave={() => saveContact(row)}
                            onCancel={() => {
                              setContact(null);
                              focusRowElement(row.key)?.focus();
                            }}
                          />
                        )}
                        {driftScan && (
                          <DriftFindings
                            key={`${row.key}-${drift?.preselect}`}
                            scan={driftScan}
                            existing={openDriftFindings(
                              data.tasks,
                              driftScan.companyId,
                            )}
                            preselect={!!drift?.preselect}
                            onClose={() => {
                              setDrift(null);
                              focusRowElement(row.key)?.focus();
                            }}
                            onCreate={async (findings) => {
                              const n = await actions.createDriftTasks(
                                row,
                                findings,
                                row.ownerId ?? data.me,
                              );
                              if (n > 0) {
                                announce(
                                  n === 1
                                    ? `1 oppgave laget på ${row.title}`
                                    : `${n} oppgaver laget på ${row.title}`,
                                );
                              }
                            }}
                          />
                        )}
                      </FocusRowItem>
                    );
                  })}
                </ul>
              )}
              {!prefs.hideShortcutHint && now.length > 0 && (
                <ShortcutHint
                  onHide={() => prefs.save({ hide_shortcut_hint: true })}
                />
              )}
            </section>
          )}

          {cleanup.length > 0 && (
            <Cleanup
              rows={cleanup}
              today={data.today}
              onMove={(row, date) => void actions.move(row, date)}
              onClose={(row) => void actions.complete(row)}
              onMoveAll={() =>
                void actions.moveAll(cleanup, nextMonday(data.today))
              }
              onOpen={(row) => navigate(row.path)}
              onPlanWeek={() => navigate(routePaths.weekPlan)}
            />
          )}
        </>
      )}

      {isMobile && sheetRow && (
        <MobileRowSheet
          row={sheetRow}
          menu={menuFor(sheetRow).filter(
            (item) =>
              item === "separator" ||
              !("onSelect" in item) ||
              item.label !== "Vis kundepanel",
          )}
          onClose={() => setSheetRow(null)}
        />
      )}
      {isMobile && contactRow && contact && (
        <Drawer
          open
          onOpenChange={(open) => {
            if (!open) setContact(null);
          }}
        >
          <DrawerContent>
            <DrawerHeader className="text-left">
              <DrawerTitle>Registrer kontakt</DrawerTitle>
              <DrawerDescription>
                {contactRow.companyName ?? contactRow.title}
              </DrawerDescription>
            </DrawerHeader>
            <div className="max-h-[70vh] overflow-y-auto pb-4">
              <ContactForm
                id="kontakt-mobil"
                draft={contact.draft}
                error={contact.error}
                isDeal={dealOf(contactRow) != null}
                onChange={(draft) =>
                  setContact({ ...contact, draft, error: null })
                }
                onSave={() => saveContact(contactRow)}
                onCancel={() => setContact(null)}
              />
            </div>
          </DrawerContent>
        </Drawer>
      )}

      {planDeal && (
        <DealNextStepDialog deal={planDeal} onClose={() => setPlanDeal(null)} />
      )}
      {planCompany != null && (
        <CompanyStepDialog
          companyId={planCompany}
          onClose={() => setPlanCompany(null)}
        />
      )}
      <Dialog
        open={timerRow != null}
        onOpenChange={(open) => !open && setTimerRow(null)}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Stopp den andre og start denne?</DialogTitle>
            <DialogDescription>
              En timer går allerede. Den stoppes og lagres før timeren på{" "}
              {timerRow
                ? `«${workItemOf(timerRow)?.title ?? timerRow.title}»`
                : "denne oppgaven"}{" "}
              starter.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setTimerRow(null)}>
              Avbryt
            </Button>
            <Button
              onClick={() => {
                const item = timerRow ? workItemOf(timerRow) : null;
                setTimerRow(null);
                if (item) {
                  void timer
                    .start(item.id)
                    .then(() => announce(`Timer startet: ${item.title}`));
                }
              }}
            >
              Stopp og start
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </FocusLayout>
  );
};

// Kundens neste steg fra Oversikt: samme dialog som på kundekortet.
const CompanyStepDialog = ({
  companyId,
  onClose,
}: {
  companyId: Identifier;
  onClose: () => void;
}) => {
  const { data: steps = [], isPending } = useGetList<CompanyNextStep>(
    "company_next_steps",
    {
      filter: { company_id: companyId },
      sort: { field: "created_at", order: "DESC" },
      pagination: { page: 1, perPage: 20 },
    },
  );
  if (isPending) return null;
  const current = steps.find((s) => !s.completed_at);
  return (
    <NextStepDialog
      open
      onOpenChange={(open) => !open && onClose()}
      companyId={companyId}
      current={current}
      history={steps.filter((s) => s.completed_at)}
    />
  );
};

const FocusLayout = ({
  header,
  aside,
  children,
}: {
  header: React.ReactNode;
  aside: React.ReactNode;
  children: React.ReactNode;
}) => (
  <Page>
    <div className="grid grid-cols-1 gap-9 xl:grid-cols-[minmax(0,1fr)_384px]">
      <div className="flex min-w-0 flex-col gap-[22px]">
        {header}
        {children}
      </div>
      {aside && <aside className="flex flex-col gap-6">{aside}</aside>}
    </div>
  </Page>
);

// «God dag, Kay» og én setning med tallene i stedet for nøkkeltall-kort.
const Greeting = ({
  data,
  scope,
  rows,
}: {
  data: ReturnType<typeof useFocusData>;
  scope: Scope;
  rows: FocusRow[];
}) => {
  const { identity } = useGetIdentity();
  const now = new Date();
  const firstName = identity?.fullName?.split(" ")[0];
  const day = (now.getDay() + 6) % 7;
  const monday = addDaysKey(data.today, -day);
  const sunday = addDaysKey(monday, 6);
  const { data: entries = [] } = useGetList<TimeEntry>(
    "time_entries",
    {
      filter: {
        sales_id: identity?.id,
        "started_at@gte": `${monday}T00:00:00`,
      },
      pagination: { page: 1, perPage: 2000 },
    },
    { enabled: identity?.id != null },
  );
  const { total: projects = 0 } = useGetList("projects", {
    filter: { "archived_at@is": null },
    pagination: { page: 1, perPage: 1 },
  });
  const seconds = entries.reduce(
    (sum, e) => sum + entrySeconds(e.started_at, e.ended_at),
    0,
  );
  const overdue = rows.filter(isOverdue).length;
  const tomorrow = addDaysKey(data.today, 1);
  const restOfWeek = data.tasks.filter(
    (t) =>
      !t.completed_at &&
      !!t.due_date &&
      t.due_date >= tomorrow &&
      t.due_date <= sunday &&
      (scope !== "mine" || String(t.assignee_id) === String(data.me)),
  ).length;
  const unownedLeads = rows.filter(
    (r) => r.type === "lead" && r.unowned,
  ).length;
  const dateText = `${WEEKDAYS[now.getDay()]} ${formatDateShort(data.today)}.`;
  const b = (text: string) => (
    <strong className="font-semibold text-foreground">{text}</strong>
  );

  return (
    <div className="flex flex-col gap-1.5">
      <h1 className="text-xl font-semibold">
        {greetingFor(now.getHours())}
        {firstName ? `, ${firstName}` : ""}
      </h1>
      {!data.isPending && (
        <p className="text-sm text-muted-foreground">
          {dateText}{" "}
          {scope === "team" ? (
            <>
              Teamet har{" "}
              {b(plural(overdue, "forfalt gjøremål", "forfalte gjøremål"))} og{" "}
              {b(plural(unownedLeads, "lead uten eier", "leads uten eier"))}.
            </>
          ) : (
            <>
              Du har ført {b(formatDuration(seconds))} denne uken og har{" "}
              {b(plural(projects, "aktivt prosjekt", "aktive prosjekter"))}.{" "}
              {overdue > 0 ? (
                <>
                  {b(plural(overdue, "oppgave", "oppgaver"))}{" "}
                  {overdue === 1 ? "er" : "er"} forfalt
                </>
              ) : (
                "Ingenting er forfalt"
              )}
              {restOfWeek > 0 ? (
                <>, og {b(String(restOfWeek))} forfaller resten av uken.</>
              ) : (
                ", og ingenting forfaller resten av uken."
              )}
            </>
          )}
        </p>
      )}
    </div>
  );
};

const Controls = ({
  scope,
  mode,
  showMode,
  remembered,
  onScope,
  onMode,
}: {
  scope: Scope;
  mode: DashboardMode;
  showMode: boolean;
  remembered: boolean;
  onScope: (scope: Scope) => void;
  onMode: (mode: DashboardMode) => void;
}) => (
  <div className="flex flex-wrap items-center gap-3">
    <ToggleGroup
      type="single"
      variant="outline"
      size="sm"
      value={scope}
      onValueChange={(v) => v && onScope(v as Scope)}
      aria-label="Hvem sine gjøremål"
    >
      {SCOPES.map((s) => (
        <ToggleGroupItem key={s.value} value={s.value} className="px-3">
          {s.label}
        </ToggleGroupItem>
      ))}
    </ToggleGroup>
    {showMode && (
      <ToggleGroup
        type="single"
        variant="outline"
        size="sm"
        value={mode}
        onValueChange={(v) => v && onMode(v as DashboardMode)}
        aria-label="Vis gjøremål eller kunder"
      >
        <ToggleGroupItem value="items" className="px-3">
          Oppgaver
        </ToggleGroupItem>
        <ToggleGroupItem value="customers" className="px-3">
          Kunder
        </ToggleGroupItem>
      </ToggleGroup>
    )}
    {remembered && (
      <span className="flex items-center gap-1 text-xs text-muted-foreground">
        <History aria-hidden className="size-3.5" />
        Husket fra sist
      </span>
    )}
    <RankingPopover />
  </div>
);

const SHORTCUTS: [string[], string][] = [
  [["J", "K"], "flytt"],
  [["Enter"], "åpne panel"],
  [["F"], "fullfør"],
  [["U"], "utsett"],
  [["N"], "neste steg"],
  [["R"], "registrer kontakt"],
  [["S"], "start timer"],
  [["P"], "fest"],
  [["Esc"], "lukk"],
];

const ShortcutHint = ({ onHide }: { onHide: () => void }) => (
  <div className="hidden flex-wrap items-center gap-x-4 gap-y-1 text-[11.5px] text-muted-foreground md:flex">
    {SHORTCUTS.map(([keys, label]) => (
      <span key={label} className="flex items-center gap-1">
        {keys.map((k) => (
          <Kbd key={k}>{k}</Kbd>
        ))}{" "}
        {label}
      </span>
    ))}
    <button
      type="button"
      onClick={onHide}
      aria-label="Skjul hurtigtastene"
      title="Kan slås på igjen under Profil"
      className="ml-auto rounded p-0.5 hover:text-foreground"
    >
      <X className="size-3.5" />
    </button>
  </div>
);

// «Rydd opp» (M5): det som har ligget over 30 dager, med egne Flytt og Lukk.
const Cleanup = ({
  rows,
  today,
  onMove,
  onClose,
  onMoveAll,
  onOpen,
  onPlanWeek,
}: {
  rows: FocusRow[];
  today: string;
  onMove: (row: FocusRow, date: string) => void;
  onClose: (row: FocusRow) => void;
  onMoveAll: () => void;
  onOpen: (row: FocusRow) => void;
  onPlanWeek: () => void;
}) => {
  const movable = rows.filter((r) => dueTarget(r) != null).length;
  // På mobil er Rydd opp én linje til den åpnes (kap. 13).
  const isMobile = useIsMobile();
  const [open, setOpen] = useState(false);
  if (isMobile && !open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="flex items-center gap-2 rounded-[10px] border px-4 py-3 text-left text-[13px]"
      >
        <History aria-hidden className="size-4 text-muted-foreground" />
        <span className="font-semibold">Rydd opp</span>
        <span className="text-muted-foreground">
          {rows.length} ting over 30 dager
        </span>
        <ChevronRight aria-hidden className="ml-auto size-4" />
      </button>
    );
  }
  return (
    <section aria-labelledby="rydd-opp-title" className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2
          id="rydd-opp-title"
          className="flex items-center gap-2 text-[13px] font-semibold"
        >
          <History aria-hidden className="size-4 text-muted-foreground" />
          Rydd opp
          <span className="font-normal text-muted-foreground">
            {plural(rows.length, "ting har", "ting har")} ligget i over 30 dager
          </span>
        </h2>
        <div className="flex items-center gap-1">
          <Button
            variant="ghost"
            size="sm"
            onClick={onPlanWeek}
            className="hidden lg:inline-flex"
          >
            <CalendarDays aria-hidden className="size-4" />
            Planlegg uken
          </Button>
          {movable > 0 && (
            <Button variant="ghost" size="sm" onClick={onMoveAll}>
              Flytt alle til neste uke
            </Button>
          )}
        </div>
      </div>
      <ul className="overflow-hidden rounded-[10px] border bg-card">
        {rows.map((row) => {
          const deal = dealOf(row);
          const due = dueTarget(row);
          const stalled = row.signals.find((s) => s.kind === "deal_stalled");
          const overdueLong =
            !!row.markerDate &&
            (row.signals.find(
              (s) =>
                s.kind === "task_overdue" || s.kind === "next_step_overdue",
            )?.days ?? 0) > 30;
          const when = overdueLong
            ? `Forfalt ${formatDateShort(row.markerDate)}`
            : stalled
              ? `Stille i ${stalled.days} dager`
              : row.markerDate
                ? `Forfalt ${formatDateShort(row.markerDate)}`
                : "";
          return (
            <li
              key={row.key}
              className="flex min-h-12 items-center gap-3 border-b px-4 py-2 text-[13px] last:border-b-0"
            >
              <button
                type="button"
                onClick={() => onOpen(row)}
                className="min-w-0 flex-1 truncate text-left hover:underline"
                title={row.title}
              >
                {row.title}
              </button>
              <span className="hidden w-40 shrink-0 truncate text-muted-foreground sm:block">
                {ROW_TYPE_LABEL[row.type]}
                {deal && row.type === "opportunity"
                  ? ` · ${row.meta[1]?.split(" i ").pop() ?? ""}`
                  : ""}
              </span>
              <span className="w-36 shrink-0 text-right text-muted-foreground tabular-nums">
                {when}
              </span>
              {due ? (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="sm">
                      Flytt
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    {QUICK_DATES(today).map((q) => (
                      <DropdownMenuItem
                        key={q.label}
                        onSelect={() => onMove(row, q.date)}
                      >
                        {q.label} ({formatDateShort(q.date)})
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
              ) : (
                <Button variant="ghost" size="sm" onClick={() => onOpen(row)}>
                  Utsett
                </Button>
              )}
              <Button
                variant="ghost"
                size="sm"
                onClick={() => (due ? onClose(row) : onOpen(row))}
                title={
                  due ? "Marker som fullført" : "Åpne for å markere som tapt"
                }
              >
                Lukk
              </Button>
            </li>
          );
        })}
      </ul>
    </section>
  );
};

// Notatet i høyre kolonne: «Aktuelt nå» (widget 1). «Langsiktig» (widget 2)
// ligger i notatets meny (M4).
const NoteCard = () => {
  const [widget, setWidget] = useState(1);
  const other = widget === 1 ? 2 : 1;
  return (
    <Suspense fallback={<Skeleton className="h-40 w-full rounded-[10px]" />}>
      <DashboardWidget
        key={widget}
        widgetId={widget}
        standardNavn={widget === 1 ? "Aktuelt nå" : "Langsiktig"}
        className="rounded-[10px] shadow-none"
        menu={
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                className="h-7 px-2 text-muted-foreground"
                aria-label="Flere notater"
              >
                …
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onSelect={() => setWidget(other)}>
                {other === 2 ? "Vis Langsiktig" : "Vis Aktuelt nå"}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        }
      />
    </Suspense>
  );
};

// Ny instans uten kunder og leads (fiks 10): tre rene lenker, ingen
// nummererte steg og ingen illustrasjon.
const NewInstance = () => {
  const [syncOpen, setSyncOpen] = useState(false);
  return (
    <section className="flex flex-col gap-3 rounded-[10px] border p-6">
      <h2 className="text-[13px] font-semibold">
        Mimin er klar, men har ingen data ennå
      </h2>
      <ul className="flex flex-col gap-2 text-sm">
        <li>
          <Link to="/companies/create" className="underline underline-offset-4">
            Legg til første kunde
          </Link>
        </li>
        <li>
          <Link
            to="/settings#lead-intake"
            className="underline underline-offset-4"
          >
            Koble til lead-innboksen
          </Link>
        </li>
        <li>
          <button
            type="button"
            onClick={() => setSyncOpen(true)}
            className="underline underline-offset-4"
          >
            Hent kunder fra Tripletex
          </button>
        </li>
      </ul>
      {syncOpen && (
        <TripletexSyncDialog open onClose={() => setSyncOpen(false)} />
      )}
    </section>
  );
};

// «Du er à jour» (kap. 8): neste frist og neste rutine. I en filtrert visning
// uten treff: «Ingen treff i denne visningen» med lenke til Team.
const UpToDate = ({
  scope,
  tasks,
  today,
  onTeam,
  isAdmin,
  hasRowsElsewhere,
}: {
  scope: Scope;
  tasks: ReturnType<typeof useFocusData>["tasks"];
  today: string;
  onTeam: () => void;
  isAdmin: boolean;
  hasRowsElsewhere: boolean;
}) => {
  if (scope !== "team" && hasRowsElsewhere) {
    return (
      <section className="rounded-[10px] border p-6 text-sm">
        <p className="font-semibold">Ingen treff i denne visningen</p>
        <button
          type="button"
          onClick={onTeam}
          className="mt-2 text-muted-foreground underline underline-offset-4 hover:text-foreground"
        >
          Vis Team
        </button>
      </section>
    );
  }
  const next = tasks
    .filter((t) => !t.completed_at && t.due_date && t.due_date > today)
    .sort((a, b) => a.due_date!.localeCompare(b.due_date!))[0];
  const monday = nextMonday(today);
  return (
    <section className="flex flex-col gap-2 rounded-[10px] border p-6 text-sm">
      <p className="font-semibold">
        {scope === "team" ? "Teamet er à jour" : "Du er à jour"}
      </p>
      <p className="text-muted-foreground">
        {next
          ? `Neste frist: ${next.title}, ${formatDateShort(next.due_date)}.`
          : "Ingen frister fremover."}
      </p>
      {isAdmin && (
        <p className="text-muted-foreground">
          Neste rutine: Gjennomgang av ufakturert arbeid, mandag{" "}
          {formatDateShort(monday)} kl. 07.
        </p>
      )}
      <Link
        to={routePaths.weekPlan}
        className="hidden w-fit items-center gap-1.5 text-muted-foreground underline underline-offset-4 hover:text-foreground lg:flex"
      >
        <CalendarDays aria-hidden className="size-4" />
        Planlegg uken
      </Link>
    </section>
  );
};
